import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import AppLoading from "verticales/AppLoading.tsx";
import MDButton from "components/MDButton";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableMaestros from "../../../verticales/TableMaestrosInformes.tsx";

const ViewClientes = ({ data = [], loading = false }) => {
  const [loadingState, setLoadingState] = useState(true); // Estado de carga para el modal
  const [openModal, setOpenModal] = useState(false);
  const [tableData, setTableData] = useState([]); // Asegúrate de que esto sea un arreglo vacío
  const [modalContent, setModalContent] = useState(null);
  const [selectedCuenta, setSelectedCuenta] = useState(""); // Para mantener la cuenta seleccionada

  const loadData = async () => {
    setLoadingState(true); // Inicia la carga
    try {
      const formattedData = data.map((item) => ({
        ...item,
        fecha_creacion: item.fecha_creacion.split("T")[0], // Formatea la fecha
      }));
      setTableData(formattedData); // Asigna directamente los datos sin agrupar
    } catch (error) {
      console.error("Error al procesar los datos:", error); // Agrega un manejo de errores adecuado
    } finally {
      setLoadingState(false); // Finaliza la carga
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "cuenta",
        header: "Cuenta",
        size: 150,
        filterVariant: "multi-select",
        filterSelectOptions: data
          .map((item) => item.cuenta)
          .filter((value, index, self) => self.indexOf(value) === index),
      },
      {
        accessorKey: "name_database",
        header: "Name Database",
        size: 200,
      },
      {
        accessorKey: "producto",
        header: "Producto",
        size: 150,
        filterVariant: "multi-select",
        filterSelectOptions: Array.from(new Set(data.map((row) => row.producto))),
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || filterValue.length === 0) {
            return true; // Si no hay filtro, muestra todos los datos
          }
          return filterValue.includes(row.getValue(columnId)); // Aplica filtro exacto
        },
      },
      {
        accessorKey: "fecha_creacion",
        header: "Fecha Creación",
        size: 200,
      },
    ],
    [data] // Dependencia de data en lugar de tableData
  );

  useEffect(() => {
    if (data.length > 0) {
      loadData();
    } else {
      console.warn("No se recibieron datos iniciales");
      setTableData([]); // Asegúrate de que tableData esté vacío si no hay datos
      setLoadingState(true);
    }
  }, [data]);

  const handleOpenModal = (row) => {
    setSelectedCuenta(row.original.cuenta); // Guardar cuenta seleccionada
    setModalContent(row.original || []); // Mostrar todos los detalles de la fila seleccionada
    setLoadingState(false); // Iniciar carga
    setOpenModal(true);
    setTimeout(() => setLoadingState(false), 2000); // Simulando carga de 2 segundos
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalContent(null);
    setSelectedCuenta(""); // Limpiar la cuenta seleccionada al cerrar el modal
  };

  const handleExportData = () => {
    console.log("Exportar datos");
  };

  return (
    <Box sx={{ marginTop: "1px" }}>
      {loadingState ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <AppLoading titleCustom={"Informe Clientes"} />
        </Box>
      ) : (
        <TableMaestros
          height={250}
          arrayColumns={columns}
          data={tableData}
          renderDetailPanel={(row) => (
            <Box sx={{ padding: "16px" }}>
              <Typography variant="body2">{row.original.razon_social || "N/A"}</Typography>
            </Box>
          )}
          enableGrouping
          enableStickyHeader
          enableColumnResizing
          muiTableContainerProps={{
            sx: { maxHeight: "calc(100vh - 445px)", minHeight: "150px" },
          }}
          muiTableBodyCellProps={{
            sx: {
              borderRight: "1px solid #e0e0e0",
              fontSize: 14,
              padding: "0.5rem",
            },
          }}
          muiTableHeadCellProps={{
            sx: (theme) => ({
              backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#303030",
              fontSize: "14px",
              fontWeight: "bold",
            }),
          }}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", padding: "4px" }}>
              <MDButton
                onClick={handleExportData}
                startIcon={<FileDownloadIcon />}
                sx={{ fontSize: "12px", fontWeight: "normal" }}
              >
                Exportar Datos
              </MDButton>
            </Box>
          )}
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 50 },
            columnFilters: [
              { id: "producto", value: [] }, // Sin filtro inicial (mostrar todos)
            ],
          }}
        />
      )}
    </Box>
  );
};

ViewClientes.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      cuenta: PropTypes.string.isRequired,
      name_database: PropTypes.string,
      producto: PropTypes.string.isRequired,
      razon_social: PropTypes.string,
      fecha_creacion: PropTypes.string.isRequired,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default ViewClientes;
